import { Theme } from '@mui/material/styles'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import {
  blueDarkTheme,
  blueTheme,
  darkTheme,
  greenDarkTheme,
  greenTheme,
  lightTheme,
  pinkDarkTheme,
  pinkTheme,
  purpleDarkTheme,
  purpleTheme,
} from '../theme'

type ColorOption = 'default' | 'green' | 'blue' | 'pink' | 'purple'

interface ThemeContextType {
  theme: Theme
  isDark: boolean
  colorTheme: ColorOption
  toggleDarkMode: () => void
  setColorTheme: (color: ColorOption) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const getTheme = (colorTheme: ColorOption, isDark: boolean): Theme => {
  const themeMap = {
    default: isDark ? darkTheme : lightTheme,
    green: isDark ? greenDarkTheme : greenTheme,
    blue: isDark ? blueDarkTheme : blueTheme,
    pink: isDark ? pinkDarkTheme : pinkTheme,
    purple: isDark ? purpleDarkTheme : purpleTheme,
  }

  return themeMap[colorTheme]
}

export const ThemeContextProvider = ({ children }: { children: ReactNode }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const saved = localStorage.getItem('theme-mode')
    return saved ? JSON.parse(saved) : false
  })

  const [colorTheme, setColorTheme] = useState<ColorOption>(() => {
    const saved = localStorage.getItem('theme-color')
    return (saved as ColorOption) || 'default'
  })

  useEffect(() => {
    localStorage.setItem('theme-mode', JSON.stringify(isDark))
  }, [isDark])

  useEffect(() => {
    localStorage.setItem('theme-color', colorTheme)
  }, [colorTheme])

  const theme = getTheme(colorTheme, isDark)

  const toggleDarkMode = () => setIsDark((prev) => !prev)

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDark,
        colorTheme,
        toggleDarkMode,
        setColorTheme,
      }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeContextProvider')
  }
  return context
}
